import React, { useLayoutEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllTests,
  addNewTest,
  deleteTest,
  updateTest,
} from "../actions/testActions";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import FormField from "../components/FormField";

const format_rupees = val =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(val);

const Tests = () => {
  const [show, setShow] = useState(false);
  const testList = useSelector(state => state.testList.data);
  const loading = useSelector(state => state.testList.loading);
  const { userInfo } = useSelector(state => state.userLogin);
  const [testCode, setTestCode] = useState("");
  const [testName, setTestName] = useState("");
  const [testPrice, setTestPrice] = useState("");
  const [testData, setTestData] = useState("");
  const dispatch = useDispatch();
  const { SearchBar } = Search;

  useLayoutEffect(() => {
    dispatch(getAllTests());
  }, [dispatch]);

  const clearFields = () => {
    setTestCode("");
    setTestName("");
    setTestPrice("");
    setTestData("");
  };

  const handleClose = () => {
    clearFields();
    setShow(false);
  };
  const handleShow = () => setShow(true);

  const handleSave = () => {
    dispatch(addNewTest({ testCode, testName, testPrice }));
    clearFields();
    setShow(false);
  };

  const editTestentry = (val, index) => {
    setTestCode(val.test_code);
    setTestName(val.test_name);
    setTestPrice(val.test_price);
    setTestData({ testID: val.id, index });
    setShow(true);
  };

  const updateTestentry = () => {
    dispatch(updateTest({
      testID: testData.testID,
      testCode,
      testName,
      testPrice,
    }, testData.index));
    clearFields();
    setShow(false);
    dispatch(getAllTests());
  };

  const deleteTestentry = (id, index) => {
    if (window.confirm("Are you sure you want to delete this test?")) {
      dispatch(deleteTest(id, index));
      dispatch(getAllTests());
    }
  };

  const columns = [
    {
      dataField: "test_code",
      text: "Test Code",
      sort: true,
      headerStyle: { width: '180px' }
    },
    {
      dataField: "test_name",
      text: "Test Name",
      sort: true,
    },
    {
      dataField: "test_price",
      text: "MRP",
      headerAlign: 'right',
      sort: true,
      headerStyle: { width: '150px' },
      style: { textAlign: 'right' },
      formatter: cell => format_rupees(cell)
    },
    {
      dataField: "id",
      text: "Action",
      editable: false,
      headerAlign: 'center',
      style: { textAlign: 'center' },
      headerStyle: { width: '150px' },
      searchable: false,
      formatter: (cellContent, row, index) => (
        <div className="d-flex justify-content-center align-items-center" style={{ gap: '8px' }}>
          <Button
            variant="link"
            title="Edit Test"
            className="p-0 text-warning"
            onClick={() => { !userInfo.email.includes("labinvoices.com") && editTestentry(row, index) }}
            disabled={userInfo.email.includes("labinvoices.com")}
          >
            <i className="fa fa-pencil-square fa-lg"></i>
          </Button>
          <Button
            variant="link"
            title="Delete Test"
            className="p-0 text-danger"
            onClick={() => { !userInfo.email.includes("labinvoices.com") && deleteTestentry(row.id, index) }}
            disabled={userInfo.email.includes("labinvoices.com")}
          >
            <i className="fa fa-trash fa-lg"></i>
          </Button>
        </div>
      ),
    },
  ];

  const validFields = !(testCode && testName && testPrice);

  return (
    <div className="tests-screen pb-5">
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <h3 className="font-weight-bold grey-900 mb-1" style={{ letterSpacing: '-0.8px', fontSize: 'clamp(1rem, 4vw, 1.4rem)' }}>Master Data</h3>
          <p className="text-muted mb-0">Manage test codes, names, and pricing</p>
        </div>
        {!userInfo.email.includes("labinvoices.com") && (
          <Button
            onClick={handleShow}
            variant="primary"
            style={{ fontWeight: 600, height: '40px', padding: '0 24px', borderRadius: '8px' }}
          >
            <i className="fa fa-plus-circle mr-2"></i> Add New Test
          </Button>
        )}
      </div>

      <div className="billing-form-card custom-card-boxshadow p-0 overflow-hidden">
        {!loading && testList ? (
          <ToolkitProvider
            keyField="id"
            data={testList}
            columns={columns}
            search
          >
            {props => (
              <div className="premium-table-container">
                <div className="premium-table-header d-flex justify-content-between align-items-center">
                  <span className="small font-weight-bold text-muted text-uppercase letter-spacing-1">Test Catalog</span>
                  <div className="search-bar-container" style={{ width: '300px' }}>
                    <SearchBar {...props.searchProps} placeholder="Search by name or code..." />
                  </div>
                </div>
                <BootstrapTable
                  {...props.baseProps}
                  bordered={false}
                  hover
                  noDataIndication={() => (
                    <div className="p-5 text-center text-muted italic">
                      <i className="fa fa-info-circle mr-2"></i> No tests found in the catalog.
                    </div>
                  )}
                  pagination={paginationFactory({
                    showTotal: true,
                    sizePerPage: 10,
                    hideSizePerPage: true,
                    className: "premium-pagination"
                  })}
                  wrapperClasses="table-responsive"
                />
              </div>
            )}
          </ToolkitProvider>
        ) : (
          <div className="p-5 text-center">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-3 text-muted">Fetching test list...</p>
          </div>
        )}
      </div>
      <Modal centered show={show} onHide={handleClose} backdrop="static">
        <Modal.Header className="bg-light pb-3" closeButton>
          <Modal.Title className="font-weight-bold grey-900">
            {testData && testData.testID ? "Edit Test" : "Add New Test"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Row>
            <Col md={6}>
              <FormField label="Test Code" mandatory>
                <Form.Control
                  type="text"
                  className="bg-light border-0"
                  placeholder="e.g. CBC"
                  value={testCode}
                  onChange={e => setTestCode(e.target.value)}
                />
              </FormField>
            </Col>
            <Col md={6}>
              <FormField label="MRP (INR)" mandatory>
                <Form.Control
                  value={testPrice}
                  type="number"
                  className="bg-light border-0"
                  placeholder="0.00"
                  min={0}
                  onChange={e => setTestPrice(e.target.value)}
                />
              </FormField>
            </Col>
            <Col md={12}>
              <FormField label="Test Name" mandatory>
                <Form.Control
                  type="text"
                  className="bg-light border-0"
                  placeholder="Enter full test name..."
                  value={testName}
                  onChange={e => setTestName(e.target.value)}
                />
              </FormField>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="bg-light border-0 p-3">
          <Button variant="outline-secondary" style={{ fontWeight: 600, height: '40px', padding: '0 24px', borderRadius: '8px' }} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            disabled={validFields}
            variant="primary"
            style={{ fontWeight: 600, height: '40px', padding: '0 24px', borderRadius: '8px' }}
            onClick={testData && testData.testID ? updateTestentry : handleSave}
          >
            {testData && testData.testID ? "Update Test" : "Save Test"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Tests;
